/**
 * Ray walking algorithm for green edge detection
 * This is the core algorithm that walks outward from the green center
 * and determines where the green boundary is based on color analysis
 */

import type { EdgeParams, ParamLevel } from './parameterSelection'
import { moveLevel, RAY_WALK_DISTANCE_LIMITS_M } from './parameterSelection'
import { squareDeMetrics, rgbaToLab } from './colorProcessing'
import { percentileAxis0 } from './dataUtils'
import { angleDeg } from './geometry'

/**
 * Result of a single ray walk step
 */
export interface RayStep {
  label: string
  step: number
  d: number
  n: number
  ratioAb: number
  minRatioAbUsed: number
  shiftAb: number
  deMed: number
  lDiff: number
  lStep: number
  medLab: [number, number, number]
  centerPx: [number, number]
  bbox: [number, number, number, number]
  ok: boolean
  forcedOk: boolean
  stopReasons: string[]
}

/**
 * Result of a complete ray walk in one direction
 */
export interface RayWalkResult {
  baseLab: [number, number, number]
  lastGood: RayStep | null
  firstBad: RayStep | null
  rows: RayStep[]
  dStart: number
  step: number
  dMax: number
  ranToEnd: boolean
}

/**
 * Ray walk end state classification
 */
export type EndState = 'no_last_good' | 'stopped' | 'ran_to_end' | 'unknown'

/**
 * Sample LAB pixels from a square patch.
 * Returns [labPixels (Nx3 Float32Array), bbox] or [null, bbox].
 */
export function squareLabPixels(
  roiRgba: ImageData,
  W: number,
  H: number,
  validMask: Uint8Array | null,
  centerX: number,
  centerY: number,
  patchHalf: number,
): [Float32Array | null, [number, number, number, number]] {
  const cx = Math.round(centerX)
  const cy = Math.round(centerY)
  const x0 = Math.max(0, cx - patchHalf)
  const y0 = Math.max(0, cy - patchHalf)
  const x1 = Math.min(W, cx + patchHalf + 1)
  const y1 = Math.min(H, cy + patchHalf + 1)
  const bbox: [number, number, number, number] = [x0, y0, x1, y1]

  if (x1 <= x0 || y1 <= y0) {
    return [null, bbox]
  }

  // Get the actual pixel data from ImageData
  const data = roiRgba.data

  const pixels: number[] = []
  for (let py = y0; py < y1; py++) {
    for (let px = x0; px < x1; px++) {
      if (validMask && validMask[py * W + px] === 0) {
        continue
      }
      const idx = (py * W + px) * 4
      pixels.push(data[idx], data[idx + 1], data[idx + 2], data[idx + 3])
    }
  }

  if (pixels.length === 0) {
    return [null, bbox]
  }

  const n = pixels.length / 4
  const rgba = new Uint8ClampedArray(pixels)
  return [rgbaToLab(rgba, n), bbox]
}

/**
 * Core ray-walking algorithm
 */
/* Walk a single direction from the anchor point, sampling LAB patches.
 * Stops when color metrics exceed thresholds (edge of green detected).
 *
 * This is the core edge-detection algorithm.
 */
export function walkDirectionDeVerbose(
  anchorXy: [number, number],
  dirVec: [number, number],
  radius: number,
  samplingRadius: number,
  patchHalf: number,
  stepPx: number,
  roiRgba: ImageData,
  W: number,
  H: number,
  validMask: Uint8Array | null,
  minPix: number,
  params: EdgeParams,
  forceFirstStep: boolean,
  mpp: number,
  label = 'DIR',
): RayWalkResult | null {
  const [ax, ay] = anchorXy
  const norm = Math.sqrt(dirVec[0] ** 2 + dirVec[1] ** 2)
  const vx = dirVec[0] / norm
  const vy = dirVec[1] / norm

  const minStartPx = Math.ceil(RAY_WALK_DISTANCE_LIMITS_M.minStart / mpp)
  const minStepPx = Math.ceil(RAY_WALK_DISTANCE_LIMITS_M.minStep / mpp)
  const maxStartPx = Math.floor(RAY_WALK_DISTANCE_LIMITS_M.maxStart / mpp)
  const samplingRadiusPx = Math.max(1, samplingRadius)
  const configuredStepPx = Math.max(1, Math.round(stepPx))

  // Search radius controls how far rays can continue, not the sampling cadence.
  let dStart = Math.max(minStartPx, Math.round(params.dStartFrac * samplingRadiusPx))
  dStart = Math.min(dStart, maxStartPx, Math.max(1, radius))
  const step = Math.max(minStepPx, configuredStepPx)
  let dMax = Math.round(params.dMaxFrac * radius)
  dMax = Math.max(dStart, dMax)

  // Get baseline LAB from first sample
  const c0x = ax + vx * dStart
  const c0y = ay + vy * dStart
  const [lab0] = squareLabPixels(roiRgba, W, H, validMask, c0x, c0y, patchHalf)
  if (!lab0 || lab0.length / 3 < minPix) {
    return null
  }

  const n0 = lab0.length / 3
  const baseLab = percentileAxis0(lab0, n0, 50)
  const baseL = baseLab[0]

  let lastGood: RayStep | null = null
  let firstBad: RayStep | null = null
  const rows: RayStep[] = []
  let prevLm: number | null = null
  let prevA: number | null = null
  let prevB: number | null = null
  let stepIdx = 0
  let ranToEnd = true

  for (let d = dStart; d <= dMax; d += step) {
    stepIdx++
    const cx = ax + vx * d
    const cy = ay + vy * d

    const [labPx, bbox] = squareLabPixels(roiRgba, W, H, validMask, cx, cy, patchHalf)
    if (!labPx) {
      continue
    }
    const n = labPx.length / 3
    if (n < minPix) {
      continue
    }

    const m = squareDeMetrics(labPx, baseLab, params.abGate, params.Lw)
    const [Lm, am, bm] = m.medLab

    const daBase = am - baseLab[1]
    const dbBase = bm - baseLab[2]
    const daStep = prevA === null ? 0 : am - prevA
    const dbStep = prevB === null ? 0 : bm - prevB
    const absStepSum = Math.abs(daStep) + Math.abs(dbStep)
    const absBaseSum = Math.abs(daBase) + Math.abs(dbBase)

    const lDiff = Lm - baseL
    const LAbs = Math.abs(lDiff)
    const lStep = prevLm === null ? 0 : Math.abs(Lm - prevLm)

    // Check all stop conditions
    const stopReasons: string[] = []

    if (m.deMed > params.deMedStop) {
      stopReasons.push(`de_med ${m.deMed.toFixed(1)} > ${params.deMedStop.toFixed(1)}`)
    }
    if (m.ratioAb < params.minRatioAb) {
      stopReasons.push(`ratio_ab ${m.ratioAb.toFixed(2)} < ${params.minRatioAb.toFixed(2)}`)
    }
    if (m.medShiftAb > params.maxMedShiftAb) {
      stopReasons.push(`shift_ab ${m.medShiftAb.toFixed(1)} > ${params.maxMedShiftAb.toFixed(1)}`)
    }
    if (LAbs > params.lAbsStop) {
      stopReasons.push(`|L_diff| ${LAbs.toFixed(1)} > ${params.lAbsStop.toFixed(1)}`)
    }
    if (lStep > params.lStepStop) {
      stopReasons.push(`|ΔL_step| ${lStep.toFixed(1)} > ${params.lStepStop.toFixed(1)}`)
    }
    if (Math.abs(daStep) > params.abStepStop || Math.abs(dbStep) > params.abStepStop) {
      stopReasons.push(`|Δa_step| or |Δb_step| exceeded ${params.abStepStop}`)
    }
    if (Math.abs(daBase) > params.abBaseStop || Math.abs(dbBase) > params.abBaseStop) {
      stopReasons.push(`|Δa_base| or |Δb_base| exceeded ${params.abBaseStop}`)
    }
    if (absStepSum > params.abStepSumStop) {
      stopReasons.push(`|Δab_step_sum| ${absStepSum.toFixed(1)} > ${params.abStepSumStop}`)
    }
    if (absBaseSum > params.abBaseSumStop) {
      stopReasons.push(`|Δab_base_sum| ${absBaseSum.toFixed(1)} > ${params.abBaseSumStop}`)
    }

    let ok = stopReasons.length === 0
    let forcedOk = false

    if (forceFirstStep && stepIdx === 1 && !ok) {
      forcedOk = true
      ok = true
    }

    const row: RayStep = {
      label,
      step: stepIdx,
      d,
      n,
      ratioAb: m.ratioAb,
      minRatioAbUsed: params.minRatioAb,
      shiftAb: m.medShiftAb,
      deMed: m.deMed,
      lDiff,
      lStep,
      medLab: [Lm, am, bm],
      centerPx: [Math.round(cx), Math.round(cy)],
      bbox,
      ok,
      forcedOk,
      stopReasons,
    }
    rows.push(row)

    if (ok) {
      lastGood = row
      prevLm = Lm
      prevA = am
      prevB = bm
    } else {
      firstBad = row
      ranToEnd = false
      break
    }
  }

  return {
    baseLab: [baseLab[0], baseLab[1], baseLab[2]],
    lastGood,
    firstBad,
    rows,
    dStart,
    step,
    dMax,
    ranToEnd,
  }
}

/**
 * Computes median of LAB pixels
 */
/**
 * Determines the end state of a ray walk result
 *
 * @param res - Ray walk result
 * @returns End state classification
 */
export function endState(res: RayWalkResult | null): EndState {
  if (!res || res.lastGood === null) {
    return 'no_last_good'
  }
  if (res.firstBad !== null) {
    return 'stopped'
  }
  if (res.ranToEnd) {
    return 'ran_to_end'
  }
  return 'unknown'
}

/**
 * Checks if ray ran to end without stopping
 */
export function ranToEndWithoutStop(res: RayWalkResult | null): boolean {
  return endState(res) === 'ran_to_end'
}

/**
 * Checks if ray stopped without running out of distance
 */
export function stoppedWithoutRunningOut(res: RayWalkResult | null): boolean {
  return endState(res) === 'stopped'
}

/**
 * Computes ray length in pixels from center to endpoint (single point version)
 *
 * @param edgePoint - Single endpoint
 * @param centerXy - Center point [x, y]
 * @returns Length in pixels
 */
export function rayLenPxSingle(edgePoint: [number, number], centerXy: [number, number]): number {
  const [ax, ay] = centerXy
  const [x, y] = edgePoint
  const dx = x - ax
  const dy = y - ay

  return Math.sqrt(dx * dx + dy * dy)
}

/**
 * Computes ray length in pixels from center to endpoint (map version)
 *
 * @param edgePoints - Map of direction to endpoint
 * @param centerXy - Center point [x, y]
 * @param name - Direction name
 * @returns Length in pixels, or null if not found
 */
export function rayLenPx(
  edgePoints: Record<string, [number, number]>,
  centerXy: [number, number],
  name: string,
): number | null {
  const pt = edgePoints[name]
  if (!pt) {
    return null
  }
  return rayLenPxSingle(pt, centerXy)
}

/**
 * Debug information for retry attempts
 */
export interface RetryAttempt {
  L_level: ParamLevel
  AB_level: ParamLevel
  ok: boolean
  len_mid?: number
  ratio?: number | null
  ang?: number | null
  pt?: [number, number]
  res?: RayWalkResult | null
  why?: string
}

/**
 * Debug information for retry_runner_length_spike
 */
export interface RetryDebugInfo {
  gate: string
  orig: {
    len_mid: number | null
    ratio_to_avgLR: number | null
    ang: number | null
  }
  tries: RetryAttempt[]
  best?: {
    len_mid: number | null
    ratio_to_avgLR: number | null
    ang: number | null
  }
  reason?: string
}

/**
 * Result of retry_runner_length_spike
 */
export interface RetryResult {
  improved: boolean
  bestRes: RayWalkResult | null
  bestPt: [number, number] | null
  bestAng: number | null
  debug: RetryDebugInfo
}

/**
 * Pass 3 correction for outward spikes:
 * - makes ray stricter step-by-step (similar stepping to Pass 2)
 * - reruns the ray and scores it primarily by shorter length (minimize len_mid),
 * - with secondary tie-breaker "larger angle"
 *
 * @param name - Direction name
 * @param v - Direction vector [x, y]
 * @param results - Map of direction to ray walk results
 * @param edgePoints - Map of direction to endpoints
 * @param baseLLevel - Starting L parameter level
 * @param baseABLevel - Starting AB parameter level
 * @param axPx - Anchor x coordinate in pixels
 * @param ayPx - Anchor y coordinate in pixels
 * @param runOneDirWithAdaptive - Function to run ray in one direction
 * @param buildEdgeParams - Function to build edge parameters
 * @param gateDebug - Debug gate identifier
 * @param maxTries - Maximum number of retry attempts (default: 6)
 * @returns Retry result with improved flag, best result, best point, best angle, and debug info
 */
export function retryRunnerLengthSpike(
  name: string,
  v: [number, number],
  options: {
    results: Record<string, RayWalkResult | null>
    edgePoints: Record<string, [number, number]>
    baseLLevel: ParamLevel
    baseABLevel: ParamLevel
    axPx: number
    ayPx: number
    runOneDirWithAdaptive: (name: string, v: [number, number], params: EdgeParams) => RayWalkResult | null
    buildEdgeParams: (lLevel: ParamLevel, abLevel: ParamLevel) => EdgeParams
    getNeighborNames: (name: string) => [string, string]
    gateDebug: string
    maxTries?: number
  },
): RetryResult {
  const {
    results,
    edgePoints,
    baseLLevel,
    baseABLevel,
    axPx,
    ayPx,
    runOneDirWithAdaptive,
    buildEdgeParams,
    getNeighborNames,
    gateDebug,
    maxTries = 6,
  } = options

  const [left, right] = getNeighborNames(name)
  const origRes = results[name] ?? null
  const origPt = edgePoints[name] ?? null

  if (origRes === null || origPt === null) {
    return {
      improved: false,
      bestRes: null,
      bestPt: null,
      bestAng: null,
      debug: {
        gate: gateDebug,
        orig: { len_mid: null, ratio_to_avgLR: null, ang: null },
        tries: [],
        reason: 'missing_orig',
      },
    }
  }

  const centerXy: [number, number] = [axPx, ayPx]

  /**
   * Score a point based on length metrics and angle
   */
  function score(pt: [number, number]): {
    lm: number | null
    ratio: number | null
    ang: number | null
  } {
    const tmp: Record<string, [number, number]> = { ...edgePoints }
    tmp[name] = pt

    const lm = rayLenPx(tmp, centerXy, name)
    const lL = rayLenPx(tmp, centerXy, left)
    const lR = rayLenPx(tmp, centerXy, right)

    const ang = angleDeg(tmp[left], tmp[name], tmp[right])

    const avgLR = lL === null || lR === null ? null : 0.5 * (lL + lR)
    const ratio = lm === null || avgLR === null || avgLR <= 1e-6 ? null : lm / avgLR

    return { lm, ratio, ang }
  }

  let bestRes = origRes
  let bestPt = origPt
  let { lm: bestLm, ratio: bestRatio, ang: bestAng } = score(origPt)
  let improved = false

  const dbg: RetryDebugInfo = {
    gate: gateDebug,
    orig: { len_mid: bestLm, ratio_to_avgLR: bestRatio, ang: bestAng },
    tries: [],
  }

  let curL = baseLLevel
  let curAB = baseABLevel

  for (let attempt = 0; attempt < maxTries; attempt++) {
    const nextL = moveLevel(curL, 'stricter')
    const nextAB = moveLevel(curAB, 'stricter')

    if (nextL === null && nextAB === null) {
      dbg.reason = 'no_stricter_levels'
      break
    }

    if (nextL !== null) {
      curL = nextL
    }
    if (nextAB !== null) {
      curAB = nextAB
    }

    const params = buildEdgeParams(curL, curAB)
    const resTry = runOneDirWithAdaptive(name, v, params)

    if (!resTry || resTry.lastGood === null) {
      dbg.tries.push({
        L_level: curL,
        AB_level: curAB,
        ok: false,
        why: 'no_last_good',
        res: resTry,
      })
      continue
    }

    const ptTry = resTry.lastGood.centerPx
    const { lm, ratio, ang } = score(ptTry)

    dbg.tries.push({
      L_level: curL,
      AB_level: curAB,
      ok: true,
      len_mid: lm ?? undefined,
      ratio: ratio,
      ang: ang,
      pt: ptTry,
      res: resTry,
    })

    // Determine if this attempt is better
    let better = false
    if (bestLm === null && lm !== null) {
      better = true
    } else if (lm !== null && bestLm !== null) {
      if (lm < bestLm) {
        // Primary: shorter length
        better = true
      } else if (lm === bestLm) {
        // Secondary: larger angle (tie-breaker)
        if (ang !== null && bestAng !== null && ang > bestAng) {
          better = true
        }
      }
    }

    if (better) {
      improved = true
      bestRes = resTry
      bestPt = ptTry
      bestLm = lm
      bestRatio = ratio
      bestAng = ang
    }
  }

  dbg.best = { len_mid: bestLm, ratio_to_avgLR: bestRatio, ang: bestAng }

  return {
    improved,
    bestRes,
    bestPt,
    bestAng,
    debug: dbg,
  }
}
